/**
 * Interface for profile objects that can be displayed.
 * All properties are optional to support various profile shapes.
 */
export interface DisplayableProfile {
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string;
  avatar_url?: string | null;
}

/**
 * Returns the best display name for a profile.
 * Priority: display_name > first_name + last_name > email > fallback
 *
 * @param profile - The profile object to get display name from
 * @param fallback - Optional fallback string when profile is null/undefined (default: '')
 * @returns The best available display name
 */
export function getProfileDisplayName(
  profile: DisplayableProfile | null | undefined,
  fallback: string = "",
): string {
  if (!profile) return fallback;

  if (profile.display_name) {
    return profile.display_name;
  }
  if (profile.first_name || profile.last_name) {
    return `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
  }
  return profile.email || fallback;
}

/**
 * Returns the greeting name for dashboard headers (TES-1092).
 * Priority: first_name + last_name > email local part (before '@') > fallback.
 * Shared by Dashboard and SuperAdminDashboard so the breadcrumb greeting
 * stays consistent (and the logic lives in exactly one place).
 *
 * @param profile - Profile with optional first/last name (null while loading)
 * @param email - The auth user's email (used as local-part fallback)
 * @param fallback - Final fallback, e.g. t('common.user')
 */
export function getUserGreetingName(
  profile: Pick<DisplayableProfile, "first_name" | "last_name"> | null | undefined,
  email: string | undefined,
  fallback: string,
): string {
  const profileName = profile
    ? [profile.first_name, profile.last_name].filter(Boolean).join(" ")
    : "";
  return profileName || email?.split("@")[0] || fallback;
}

/**
 * Returns initials from a profile for avatar fallbacks.
 * Priority: display_name initials > first_name + last_name initials > email prefix
 *
 * @param profile - The profile object to get initials from
 * @param fallback - Optional fallback string when profile is null/undefined (default: '??')
 * @returns 1-2 character initials in uppercase
 */
export function getProfileInitials(
  profile: DisplayableProfile | null | undefined,
  fallback: string = "??",
): string {
  if (!profile) return fallback;

  if (profile.display_name) {
    return profile.display_name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }
  if (profile.first_name || profile.last_name) {
    return `${profile.first_name?.[0] || ""}${profile.last_name?.[0] || ""}`.toUpperCase();
  }
  if (profile.email) {
    return profile.email.substring(0, 2).toUpperCase();
  }
  return fallback;
}
